import type { ReactNode } from 'react'
import { usePermission } from '@/hooks/usePermission'

/**
 * `<Can>` —— 声明式权限门控组件(advisory)。
 *
 * **它是 UX,不是安全边界**:真正拦权限的是后端 `RequirePermission`(命中即 403)。
 * `<Can>` 只负责「别把一个点了必定 403 的按钮显示给用户」。后端权限不全时,
 * 最坏后果是「该显示的按钮没显示」(误藏),不会让页面崩溃——本组件纯布尔渲染,永不抛错。
 *
 * 复用 `usePermission`:superuser 直接放行,支持精确码与通配符(`pms:*` / `pms:task:*`)。
 *
 * 三种判定方式(互斥,优先级 all > any > perm):
 * - `perm`  单个权限码,拥有即通过
 * - `any`   一组权限码,拥有任一即通过
 * - `all`   一组权限码,全部拥有才通过
 * - 都不传  视为无门控,直接渲染 children
 *
 * @example
 * <Can perm="pms:task:create"><Button>Create</Button></Can>
 *
 * <Can perm="system:role:delete" fallback={<DisabledButton tip="No permission" />}>
 *   <DeleteButton />
 * </Can>
 */
export interface CanProps {
  /** 单个权限码(精确或通配符模式) */
  perm?: string
  /** 拥有任一即通过 */
  any?: string[]
  /** 全部拥有才通过 */
  all?: string[]
  /** 通过时渲染的内容 */
  children: ReactNode
  /** 不通过时渲染的内容,默认 `null`(彻底隐藏) */
  fallback?: ReactNode
}

export function Can({ perm, any, all, children, fallback = null }: CanProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission()

  let allowed: boolean
  if (all && all.length > 0) {
    allowed = hasAllPermissions(...all)
  } else if (any && any.length > 0) {
    allowed = hasAnyPermission(...any)
  } else if (perm) {
    allowed = hasPermission(perm)
  } else {
    // 未指定任何权限 → 不门控
    allowed = true
  }

  return <>{allowed ? children : fallback}</>
}
